import {
  ButtonsGroup,
  ClearInputButton,
  DropdownNavList,
  DropdownNavListProps,
  findNavItem,
  getSelectTogglerIcon,
  Icon,
  InputProps,
  NavItem,
  NavListItemWithBlockDescription,
  Tag,
  useModal,
  useTheme,
} from '@billy/design-system'
import isBoolean from 'lodash/isBoolean'
import isEqual from 'lodash/isEqual'
import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'

import { Contact } from '@features/contacts/types/contact'
import { fetchContacts, FetchContactsQueryParams } from '@modules-deprecated/app/contacts/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { KeyboardKey } from '../../../enums/keyboardKey'
import { ModalId } from '../../../enums/modalId'
import { QueryKeys } from '../../../enums/queryKeys'
import { CreateContactModal } from '../../Modal/CreateContactModal'
import { ContactSelectDropdownFooter } from './elements/ContactSelectDropdownFooter'
import * as Styled from './styles'
import { ContactValue } from './types/contactValue'
import { filterContacts } from './utils/filterContacts'
import { getContactValue } from './utils/getContactValue'
import { getDefaultContactsFilters } from './utils/getDefaultContactsFilters'
import { getItems } from './utils/getItems'

export interface ContactSelectProps
  extends Pick<DropdownNavListProps<Partial<Contact>>, 'placement' | 'selectedId'>,
    Pick<
      InputProps,
      | 'alignment'
      | 'autoCompleted'
      | 'autoFocus'
      | 'bordered'
      | 'className'
      | 'disabled'
      | 'error'
      | 'focused'
      | 'hidden'
      | 'id'
      | 'name'
      | 'onBlur'
      | 'onChange'
      | 'onChangeDebounced'
      | 'onFocus'
      | 'onKeyDown'
      | 'onKeyPress'
      | 'onMouseDown'
      | 'onPressEnter'
      | 'placeholder'
      | 'readOnly'
      | 'selectOnFocus'
      | 'size'
      | 'success'
      | 'suffix'
      | 'type'
    > {
  // Select props
  allowClear?: boolean
  allowCreateNew?: boolean
  inputValue?: string
  isCustomer?: boolean
  isSupplier?: boolean
  newSupplierSuggestions?: Partial<Contact>[]
  onCreateNew?: (contact: Partial<Contact>) => void
  onSelect?: (id?: string, value?: Partial<Contact>) => void
  selectedValue?: Partial<Contact>
  /** if enabled, input has cursor inside and user can search for the items */
  withSearch?: boolean
  // DropdownNavList props
  dropdownId?: string
  dropdownMaxHeight?: DropdownNavListProps<Partial<Contact>>['maxHeight']
  dropdownSize?: DropdownNavListProps<Partial<Contact>>['size']
}

const PAGES_LIMIT = 2

export const ContactSelect = ({
  allowClear = false,
  allowCreateNew = false,
  inputValue: inputValueControlled,
  isCustomer,
  isSupplier,
  newSupplierSuggestions,
  onCreateNew,
  onSelect,
  selectedId: selectedIdControlled,
  selectedValue: selectedValueControlled,
  withSearch = false,
  // DropdownNavList props
  dropdownId,
  dropdownMaxHeight = 'default',
  dropdownSize = 'fitTrigger',
  placement = 'bottom-end',
  // Input props
  alignment = 'left',
  autoCompleted,
  autoFocus,
  bordered,
  className,
  disabled,
  error,
  focused = false,
  hidden,
  id,
  name,
  onBlur,
  onChange,
  onChangeDebounced,
  onFocus,
  onKeyDown,
  onKeyPress,
  onMouseDown,
  onPressEnter,
  placeholder,
  readOnly,
  selectOnFocus,
  size,
  success,
  type,
}: ContactSelectProps): ReactElement => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { organization } = useUserOrganization()
  const { open: openModal } = useModal(ModalId.CreateContactModal)
  const [itemsListProcessed, setItemsListProcessed] = useState<NavItem<ContactValue>[]>([])
  const [isFocused, setIsFocused] = useState(focused)
  const [selectedId, setSelectedId] = useState<string | undefined>(selectedIdControlled || '')
  const [selectedValue, setSelectedValue] = useState<Partial<Contact> | undefined>(selectedValueControlled)
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState(inputValueControlled || '')
  const [currentPage, setCurrentPage] = useState(1)
  const isInputActionClickedRef = useRef(false) // helper for detecting click on some action(clear, create) inside input to not open the dropdown
  const isDropdownInsideClickedRef = useRef(false) // helper for not clearing data on blur, after selecting some item (check handleInputBlur)
  const isUserEditingRef = useRef(false) // true once the user typed during the current focus, so late parent values don't overwrite the edit

  const isClearable = withSearch && allowClear && !!inputValue

  const togglerIcon = useMemo(
    () => getSelectTogglerIcon({ isOpen, isFocused, withSearch, withSearchOnly: false }),
    [isOpen, isFocused, withSearch],
  )

  const queryProps: FetchContactsQueryParams = useMemo(
    () => ({
      organizationId: organization?.id as string,
      ...(isBoolean(isSupplier) ? { isSupplier } : {}),
      ...(isBoolean(isCustomer) ? { isCustomer } : {}),
      q: inputValue,
      page: currentPage,
      pageSize: 100,
    }),
    [organization?.id, isCustomer, isSupplier, inputValue, currentPage],
  )

  const { data, isLoading, refetch } = useQuery([QueryKeys.Contacts, queryProps], () => fetchContacts(queryProps), {
    enabled: !!organization?.id,
    cacheTime: 0,
  })

  const responseFilters = useMemo(() => data?.meta?.paging, [data?.meta?.paging])

  const defaultFilters = useMemo(() => getDefaultContactsFilters(), [])

  const contactsFiltered = useMemo(() => {
    return filterContacts(data?.contacts, defaultFilters, selectedId)
  }, [data?.contacts, defaultFilters, selectedId])

  const isPagesLimitItem = useMemo(() => {
    return !inputValue?.length && currentPage === PAGES_LIMIT
  }, [inputValue, currentPage])

  const hasUnresolvedSupplierSuggestions =
    !selectedValue && !!newSupplierSuggestions && newSupplierSuggestions.length > 1

  const items = useMemo(() => {
    return getItems(
      contactsFiltered,
      t,
      isPagesLimitItem,
      hasUnresolvedSupplierSuggestions ? newSupplierSuggestions : undefined,
    )
  }, [contactsFiltered, t, isPagesLimitItem, hasUnresolvedSupplierSuggestions, newSupplierSuggestions])

  useEffect(() => {
    if (hasUnresolvedSupplierSuggestions) {
      setItemsListProcessed(items)
    } else {
      setItemsListProcessed((prevItems) => {
        const newItems = items.filter((item) => !prevItems.some((prevItem) => prevItem.id === item.id))
        return [...prevItems, ...newItems]
      })
    }
  }, [items, hasUnresolvedSupplierSuggestions])

  const openDropdown = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
  }, [])

  const toggleDropdown = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen)
  }, [])

  const handleDropdownOpen = useCallback(() => {
    openDropdown()
  }, [openDropdown])

  const handleDropdownClose = useCallback(() => {
    closeDropdown()
  }, [closeDropdown])

  const resetItemsState = useCallback(() => {
    setItemsListProcessed([])
    setCurrentPage(1)
  }, [])

  const setSelectedContact = useCallback((contact: Partial<Contact> | undefined) => {
    // Contact could be undefined in case it was changed outside the component
    setInputValue(contact?.name || '')
    setSelectedId(contact?.id || undefined)
    setSelectedValue(contact || undefined)
  }, [])

  const handleInputActionClicked = useCallback(() => {
    isInputActionClickedRef.current = true
  }, [])

  const handleCreateNew = useCallback(() => {
    openModal()
    closeDropdown()
  }, [openModal, closeDropdown])

  const selectItem = useCallback(
    (id: string | undefined, withSelectEvent = true) => {
      const item = findNavItem(itemsListProcessed, id)

      // When select same item and input value wasn't changed
      if (id !== selectedId && item?.value.name !== inputValue) {
        resetItemsState()
      }

      if (item) {
        setSelectedId(id)
        setSelectedValue(getContactValue(item))
        setInputValue(item.value.name || '')
      } else {
        setSelectedId(undefined)
        setSelectedValue(undefined)
      }

      if (withSelectEvent) {
        const valueFiltered = item?.value ? getContactValue(item) : undefined
        onSelect?.(id, valueFiltered)
      }
    },
    [itemsListProcessed, onSelect, resetItemsState, selectedId, inputValue],
  )

  const deselectItem = useCallback(() => {
    selectItem(undefined, true)
    setInputValue('')
  }, [selectItem])

  const handleClearIconMouseDown = useCallback(() => {
    if (allowClear) {
      handleInputActionClicked()
      deselectItem()
    }
  }, [allowClear, handleInputActionClicked, deselectItem])

  const handleItemClick = useCallback(
    (id: string) => {
      if (withSearch) {
        isDropdownInsideClickedRef.current = true
      }

      selectItem(id, true)
    },
    [selectItem, withSearch],
  )

  const handleInputMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (isInputActionClickedRef.current) {
        isInputActionClickedRef.current = false
        return
      }

      if (withSearch) {
        isDropdownInsideClickedRef.current = true
      }

      toggleDropdown()
      onMouseDown?.(event)
    },
    [onMouseDown, withSearch, toggleDropdown],
  )

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value

      isUserEditingRef.current = true
      resetItemsState()
      setInputValue(value)
      openDropdown()
      onChange?.(event)

      // Release the selected item once the text no longer matches it, so a new item can be selected.
      // Do not notify the parent yet: that would clear a matched supplier (and its vendor defaults) on the first keystroke.
      const isValueChanged = value !== (selectedValue?.name ?? '')
      if (selectedValue && isValueChanged) {
        selectItem(undefined, false)
      }

      // An empty field is a real clear (select-all/delete or backspacing the name away), so unlink now.
      if (!value && (selectedValue || selectedValueControlled)) {
        onSelect?.(undefined, undefined)
      }
    },
    [
      onChange,
      onSelect,
      openDropdown,
      setInputValue,
      resetItemsState,
      selectedValue,
      selectedValueControlled,
      selectItem,
    ],
  )

  const handleInputBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (withSearch) {
        if (isDropdownInsideClickedRef.current) {
          isDropdownInsideClickedRef.current = false
        } else {
          // We need to reset items and input and selectedId only when isDropdownInsideClickedRef.current = false
          if (!selectedValue && !!inputValue) {
            resetItemsState()
            setSelectedId(undefined)

            if (allowCreateNew) {
              // The text matches no existing contact, but it can still become one, so keep it as a new contact
              const newContact = { name: inputValue }

              setSelectedValue(newContact)
              onSelect?.(undefined, newContact)
            } else {
              setInputValue('')
            }
          }

          closeDropdown()
        }
      } else {
        closeDropdown()
      }

      isUserEditingRef.current = false
      setIsFocused(false)
      onBlur?.(event)
    },
    [onBlur, closeDropdown, selectedValue, withSearch, inputValue, resetItemsState, allowCreateNew, onSelect],
  )

  const handleInputFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      isUserEditingRef.current = false
      setIsFocused(true)
      onFocus?.(event)
    },
    [onFocus],
  )

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isDropdownInsideClickedRef.current) {
        isDropdownInsideClickedRef.current = false
      }

      if (event.key === KeyboardKey.ArrowDown) {
        openDropdown()
      }

      onKeyDown?.(event)
    },
    [onKeyDown, openDropdown],
  )

  const handleDropdownMouseDown = useCallback(() => {
    if (withSearch) {
      isDropdownInsideClickedRef.current = true
    }
  }, [withSearch])

  const handlePressEnter = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (allowCreateNew && !itemsListProcessed.length && !selectedId) {
        handleCreateNew()
      }

      onPressEnter?.(event)
    },
    [allowCreateNew, onPressEnter, itemsListProcessed, handleCreateNew, selectedId],
  )

  const handleScrollEnd = useCallback(() => {
    if (responseFilters && responseFilters.pageCount > currentPage) {
      if (currentPage === PAGES_LIMIT && !inputValue.length) {
        return
      }
      setCurrentPage(responseFilters.page + 1)
    }
  }, [currentPage, responseFilters, inputValue])

  const handleContactCreated = useCallback(
    async (contact: Partial<Contact>) => {
      resetItemsState()

      // We need to set all new contact data to avoid list reset in selectedValueControlled useEffect
      setSelectedContact(contact)
      onSelect?.(contact.id, contact)
      onCreateNew?.(contact)
      // Refetch contacts list to select new contact
      await refetch()
    },
    [onSelect, resetItemsState, onCreateNew, refetch, setSelectedContact],
  )

  useEffect(() => {
    if (selectedIdControlled !== undefined && selectedIdControlled !== selectedId) {
      selectItem(selectedIdControlled, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdControlled])

  useEffect(() => {
    // Only an in-progress edit blocks the incoming value; a value that just arrived still has to be applied.
    if (isUserEditingRef.current) {
      return
    }

    if (!isEqual(selectedValueControlled, selectedValue)) {
      resetItemsState()
      setSelectedContact(selectedValueControlled)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValueControlled])

  useEffect(() => {
    if (inputValueControlled !== undefined && inputValueControlled !== inputValue) {
      resetItemsState()
      setInputValue(inputValueControlled)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValueControlled])

  useEffect(() => {
    setIsFocused(focused)
  }, [focused])

  const modalDefaultValues = useMemo((): Partial<Contact> => {
    const countryId = organization?.countryId
    let defaultValues: Partial<Contact> = { ...(countryId ? { countryId } : {}) }

    if (selectedValue && !selectedValue?.id) {
      const selectedCountryId = selectedValue.countryId ?? countryId
      defaultValues = { ...selectedValue, ...(selectedCountryId ? { countryId: selectedCountryId } : {}) }
    } else if (selectedValue?.id) {
      defaultValues.name = ''
    } else {
      defaultValues.name = inputValue
    }

    return defaultValues
  }, [selectedValue, inputValue, organization?.countryId])

  const inputPrefixTag = useMemo(() => {
    if (selectedValue && !selectedValue?.id) {
      return (
        <Tag color="blue" size="sm" type="subtle">
          {t('contact_select.new_contact_tag.text')}
        </Tag>
      )
    }

    if (!selectedValue && newSupplierSuggestions && newSupplierSuggestions.length > 1) {
      return (
        <Tag color="blue" size="sm" type="subtle">
          {t('contact_select.new_suppliers_suggested_tag.text', { count: newSupplierSuggestions.length })}
        </Tag>
      )
    }

    return undefined
  }, [selectedValue, newSupplierSuggestions, t])

  return (
    <>
      <DropdownNavList
        autoToggle={false}
        id={dropdownId}
        inputSearchValue={inputValue}
        isFetching={isLoading}
        isOpen={isOpen}
        itemRender={(props) => <NavListItemWithBlockDescription {...props} />}
        items={itemsListProcessed}
        maxHeight={dropdownMaxHeight}
        onClose={handleDropdownClose}
        onItemClick={handleItemClick}
        onKeyDown={handleInputKeyDown}
        onMouseDown={handleDropdownMouseDown}
        onOpen={handleDropdownOpen}
        onScrollEnd={handleScrollEnd}
        placement={placement}
        scrollEndOffset={50}
        selectedId={selectedId}
        shouldSearchForBestMatched={withSearch}
        size={dropdownSize}
        footer={
          allowCreateNew ? (
            <ContactSelectDropdownFooter onClick={handleCreateNew} inputValue={inputValue} selectedId={selectedId} />
          ) : undefined
        }
        trigger={
          <Styled.Input
            alignment={alignment}
            autoComplete="off"
            autoCompleted={autoCompleted}
            autoFocus={autoFocus}
            bordered={bordered}
            className={className}
            clearable={isClearable}
            disabled={disabled}
            error={error}
            focused={isFocused}
            hidden={hidden}
            id={id}
            name={name}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            onChangeDebounced={onChangeDebounced}
            onMouseDown={handleInputMouseDown}
            onFocus={handleInputFocus}
            onKeyPress={onKeyPress}
            onPressEnter={handlePressEnter}
            placeholder={placeholder || `${t('company_select.dropdown.placeholder')}...`}
            prefix={inputPrefixTag}
            readOnly={readOnly}
            searchable={withSearch}
            selectLook
            selectOnFocus={selectOnFocus}
            size={size}
            success={success}
            suffix={
              <ButtonsGroup>
                {isClearable && !disabled && (
                  <Styled.ClearInputWrapper>
                    <ClearInputButton onMouseDown={handleClearIconMouseDown} inputSize={size} />
                  </Styled.ClearInputWrapper>
                )}
                <Styled.ToggleIconWrapper>
                  <Icon icon={togglerIcon} color={theme.ds.colors.base.textPrimary} />
                </Styled.ToggleIconWrapper>
              </ButtonsGroup>
            }
            title={inputValue}
            truncate
            type={type}
            value={inputValue}
          />
        }
        withNavigation
      />
      <CreateContactModal
        defaultValues={modalDefaultValues}
        isSupplier={isSupplier}
        onContactCreate={handleContactCreated}
      />
    </>
  )
}
